import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import ListComponent from "./ListComponent";
import RadioComponent from "./RadioComponent";
import TableComponent from "./TableComponent";
import TextInputComponent from "./TextComponent";
import VideoComponent from "./VideoComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Nav from "./Nav";
import HeaderTag from "./HeaderTag";
import Hgroup from "./Hgroup";
import Footer from "./Footer";
import Section from "./Section";
import Span from "./Span";
import FormComponent from "./FormComponent";
import MainComponent from "./MainComponent";
import ArticleComponent from "./ArticleComponent";
import AsideComponent from "./AsideComponent";
import HRTag from "./HRTag";
import BRTag from "./BRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";

const Division = ({ div }) => {
  const { addDivision, hoveredDivision, addElement, addCanvasDrop, files } =
    useContext(CanvasContext);
  const divisionRef = useRef(null);
  const isHovered = div.id === hoveredDivision;
  const [isDivHovered, setIsDivHovered] = useState(false);
  const [isDivFocused, setIsDivFocused] = useState(false);
  const [isDivActive, setIsDivActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableOutput",
      "DroppableMain",
      "DroppableArticle",
      "DroppableAside",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",
    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (item.type === "DroppableDiv") {
          addDivision(div.id, item.name, item.type);
        } else if (
          item.type === "DroppableNav" ||
          item.type === "DroppableHeader" ||
          item.type === "DroppableHGroup" ||
          item.type === "DroppableFooter" ||
          item.type === "DroppableSection" ||
          item.type === "DroppableSpan" ||
          item.type === "DroppableForm" ||
          item.type === "DroppableMain" ||
          item.type === "DroppableArticle" ||
          item.type === "DroppableAside"
        ) {
          addCanvasDrop(div.id, item.name, item.type);
        } else {
          addElement(div.id, item.name, item.type);
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = div;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = div;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = div;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = div;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };

  const generateKeyframes = () => {
    if (!div.keyframes || div.keyframes.length === 0) return null;

    return div.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };
  const combinedRef = useMemo(
    () => (node) => {
      divisionRef.current = node;
      drop(node);
    },
    [drop]
  );
  useEffect(() => {
    if (divisionRef.current && div.keyframes && div.keyframes.length > 0) {
      const keyframes = generateKeyframes();
      const animation = divisionRef.current.animate(keyframes, {
        duration: parseFloat(div.animationDuration) * 1000 || 1000,
        delay: parseFloat(div.animationDelay) * 1000 || 0,
        iterations:
          div.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(div.animationIterationCount) || 1,
        direction: div.animationDirection || "normal",
        fill: div.animationFillMode || "none",
        easing: div.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    div.keyframes,
    div.animationDuration,
    div.animationDelay,
    div.animationIterationCount,
    div.animationDirection,
    div.animationFillMode,
    div.animationTimingFunction,
  ]);
  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (div.backgroundImage && div.backgroundImage.startsWith("./")) {
        const imageName = div.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(div.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [div.backgroundImage, files]);

  const focusedStyles =
    isDivFocused && div.pseudoClass === "focus"
      ? {
          width: div.focusedwidth || div.width || "100%",
          height: div.focusedheight || div.height || "auto",

          padding: div.focusedpadding || div.padding || "8px",
          paddingTop: div.focusedpaddingTop || div.focusedpadding || "8px",
          paddingRight: div.focusedpaddingRight || div.focusedpadding || "8px",
          paddingBottom:
            div.focusedpaddingBottom || div.focusedpadding || "8px",
          paddingLeft: div.focusedpaddingLeft || div.focusedpadding || "8px",
          margin: div.focusedmargin || div.margin || "0px",
          marginTop: div.focusedmarginTop || div.focusedmargin || "0px",
          marginRight: div.focusedmarginRight || div.focusedmargin || "0px",
          marginBottom: div.focusedmarginBottom || div.focusedmargin || "0px",
          marginLeft: div.focusedmarginLeft || div.focusedmargin || "0px",

          borderTop:
            div.focusedborderTop ||
            div.focusedborder ||
            div.border ||
            "1px solid black",
          borderRight:
            div.focusedborderRight ||
            div.focusedborder ||
            div.border ||
            "1px solid black",
          borderBottom:
            div.focusedborderBottom ||
            div.focusedborder ||
            div.border ||
            "1px solid black",
          borderLeft:
            div.focusedborderLeft ||
            div.focusedborder ||
            div.border ||
            "1px solid black",
          borderSapcing: div.focusedborderSpacing || div.borderSpacing || "0px",
          borderCollapse:
            div.focusedborderCollapse || div.borderCollapse || "0px",
          borderRadius: div.focusedborderRadius || div.borderRadius || "0",
          outline: div.focusedoutline || div.outline || "none",

          position: div.focusedposition || div.position || "relative",
          top: div.focusedtop || div.top || "auto",
          right: div.focusedright || div.right || "auto",
          bottom: div.focusedbottom || div.bottom || "auto",
          left: div.focusedleft || div.left || "auto",
          zIndex: div.focusedzIndex || div.zIndex || "auto",
          boxShadow: div.focusedboxShadow || div.boxShadow || "none",
          opacity: div.focusedOpacity || div.opacity || 1,
          display: div.focuseddisplay || div.display || "block",
          flexDirection: div.focusedflexDirection || div.flexDirection || "column",
          flexWrap: div.focusedflexWrap || "nowrap",
          order: div.focusedorder || "0",
          flexGrow: div.focusedflexGrow || "0",
          flexShrink: div.focusedflexShrink || "1",
          flexBasis: div.focusedflexBasis || "auto",

          gridTemplateColumns: div.focusedgridTemplateColumns || "none",
          gridTemplateRows: div.focusedgridTemplateRows || "none",
          gridArea: div.focusedgridArea || "auto",
          gridAutoFlow: div.focusedgridAutoFlow || "row",
          gridAutoColumns: div.focusedgridAutoColumns || "auto",
          gridAutoRows: div.focusedgridAutoRows || "auto",
          columnGap: div.focusedcolumnGap || "0",
          rowGap: div.focusedrowGap || "0",

          justifyContent: div.focusedjustifyContent || "flex-start",
          alignItems: div.focusedalignItems || "stretch",
          alignContent: div.focusedalignContent || "stretch",
          alignSelf: div.focusedalignSelf || "auto",
         //  placeContent: div.focusedplaceContent || "normal",
         //  placeItems: div.focusedplaceItems || "normal",
          justifyItems: div.focusedjustifyItems || "stretch",
          justifySelf: div.focusedjustifySelf || "auto",

          gap: div.focusedgap || "0",
          visibility: div.focusedvisibility || "visible",

          color: div.focusedcolor || div.color || "#000000",
          backgroundColor:
            div.focusedbackgroundColor || div.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (div.focusedbackgroundImage) {
              if (div.focusedbackgroundImage.startsWith("./")) {
                const imageName = div.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${div.focusedbackgroundImage})`;
            }
            if (div.backgroundImage) {
              if (div.backgroundImage.startsWith("./")) {
                const imageName = div.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${div.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            div.focusedbackgroundPosition || div.backgroundPosition || "0% 0%",
          backgroundSize:
            div.focusedbackgroundSize || div.backgroundSize || "auto",
          backgroundRepeat:
            div.focusedbackgroundRepeat || div.backgroundRepeat || "repeat",
          backgroundAttachment:
            div.focusedbackgroundAttachment ||
            div.backgroundAttachment ||
            "scroll",

          fontFamily: div.focusedfontFamily || div.fontFamily || "inherit",
          fontSize: div.focusedfontSize || div.fontSize || "inherit",
          fontWeight: div.focusedfontWeight || div.fontWeight || "normal",
          fontStyle: div.focusedfontStyle || div.fontStyle || "normal",
          fontVariant: div.focusedfontVariant || div.fontVariant || "normal",
          lineHeight: div.focusedlineHeight || div.lineHeight || "normal",
          textShadow: div.focusedtextShadow || div.textShadow || "none",
          textAlign: div.focusedtextAlign || div.textAlign || "left",
          verticalAlign:
            div.focusedverticalAlign || div.verticalAlign || "baseline",
          textDecoration:
            div.focusedtextDecoration || div.textDecoration || "none",
          textTransform:
            div.focusedtextTransform || div.textTransform || "none",
          letterSpacing:
            div.focusedletterSpacing || div.letterSpacing || "normal",
          wordSpacing: div.focusedwordSpacing || div.wordSpacing || "normal",
          textIndent: div.focusedtextIndent || div.textIndent || "0",
          whiteSpace: div.focusedwhiteSpace || div.whiteSpace || "normal",
          textOverflow: div.focusedtextOverflow || div.textOverflow || "clip",
          direction: div.focuseddirection || div.direction || "ltr",
          unicodeBidi: div.focusedunicodeBidi || div.unicodeBidi || "normal",
          writingMode:
            div.focusedwritingMode || div.writingMode || "horizontal-tb",

          cursor: div.focusedcursor || div.cursor || "default",
          filter: div.focusedfilter || div.filter || "none",
          backdropFilter:
            div.focusedbackdropFilter || div.backdropFilter || "none",
          mixBlendMode: div.focusedmixBlendMode || div.mixBlendMode || "normal",
          aspectRatio: div.focusedaspectRatio || div.aspectRatio || "auto",

          transform: div.focusedtransform || div.transform || "none",
          transition: div.focusedtransition || div.transition || "none",
        }
      : {};

  const hoverStyles =
    isDivHovered && div.pseudoClass === "hover"
      ? {
          width: div.hoveredwidth || div.width || "100%",
          height: div.hoveredheight || div.height || "auto",

          padding: div.hoveredpadding || div.padding || "8px",
          paddingTop: div.hoveredpaddingTop || div.hoveredpadding || "8px",
          paddingRight: div.hoveredpaddingRight || div.hoveredpadding || "8px",
          paddingBottom:
            div.hoveredpaddingBottom || div.hoveredpadding || "8px",
          paddingLeft: div.hoveredpaddingLeft || div.hoveredpadding || "8px",
          margin: div.hoveredmargin || div.margin || "0px",
          marginTop: div.hoveredmarginTop || div.hoveredmargin || "0px",
          marginRight: div.hoveredmarginRight || div.hoveredmargin || "0px",
          marginBottom: div.hoveredmarginBottom || div.hoveredmargin || "0px",
          marginLeft: div.hoveredmarginLeft || div.hoveredmargin || "0px",

          borderTop:
            div.hoveredborderTop ||
            div.hoveredborder ||
            div.border ||
            "1px solid black",
          borderRight:
            div.hoveredborderRight ||
            div.hoveredborder ||
            div.border ||
            "1px solid black",
          borderBottom:
            div.hoveredborderBottom ||
            div.hoveredborder ||
            div.border ||
            "1px solid black",
          borderLeft:
            div.hoveredborderLeft ||
            div.hoveredborder ||
            div.border ||
            "1px solid black",
          borderSapcing: div.hoveredborderSpacing || div.borderSpacing || "0px",
          borderCollapse:
            div.hoveredborderCollapse || div.borderCollapse || "0px",
          borderRadius: div.hoveredborderRadius || div.borderRadius || "0",
          outline: div.hoveredoutline || div.outline || "none",

          position: div.hoveredposition || div.position || "relative",
          top: div.hoveredtop || div.top || "auto",
          right: div.hoveredright || div.right || "auto",
          bottom: div.hoveredbottom || div.bottom || "auto",
          left: div.hoveredleft || div.left || "auto",
          zIndex: div.hoveredzIndex || div.zIndex || "auto",
          boxShadow: div.hoveredboxShadow || div.boxShadow || "none",
          opacity: div.hoveredOpacity || div.opacity || 1,
          display: div.hovereddisplay || div.display || "block",
          flexDirection: div.hoveredflexDirection || div.flexDirection || "column",
          flexWrap: div.hoveredflexWrap || "nowrap",
          order: div.hoveredorder || "0",
          flexGrow: div.hoveredflexGrow || "0",
          flexShrink: div.hoveredflexShrink || "1",
          flexBasis: div.hoveredflexBasis || "auto",

          gridTemplateColumns: div.hoveredgridTemplateColumns || "none",
          gridTemplateRows: div.hoveredgridTemplateRows || "none",
          gridArea: div.hoveredgridArea || "auto",
          gridAutoFlow: div.hoveredgridAutoFlow || "row",
          gridAutoColumns: div.hoveredgridAutoColumns || "auto",
          gridAutoRows: div.hoveredgridAutoRows || "auto",
          columnGap: div.hoveredcolumnGap || "0",
          rowGap: div.hoveredrowGap || "0",

          justifyContent: div.hoveredjustifyContent || "flex-start",
          alignItems: div.hoveredalignItems || "stretch",
          alignContent: div.hoveredalignContent || "stretch",
          alignSelf: div.hoveredalignSelf || "auto",
         //  placeContent: div.hoveredplaceContent || "normal",
         //  placeItems: div.hoveredplaceItems || "normal",
          justifyItems: div.hoveredjustifyItems || "stretch",
          justifySelf: div.hoveredjustifySelf || "auto",

          gap: div.hoveredgap || "0",
          visibility: div.hoveredvisibility || "visible",

          color: div.hoveredcolor || div.color || "#000000",
          backgroundColor:
            div.hoveredbackgroundColor || div.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (div.hoveredbackgroundImage) {
              if (div.hoveredbackgroundImage.startsWith("./")) {
                const imageName = div.hoveredbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${div.hoveredbackgroundImage})`;
            }
            if (div.backgroundImage) {
              if (div.backgroundImage.startsWith("./")) {
                const imageName = div.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${div.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            div.hoveredbackgroundPosition || div.backgroundPosition || "0% 0%",
          backgroundSize:
            div.hoveredbackgroundSize || div.backgroundSize || "auto",
          backgroundRepeat:
            div.hoveredbackgroundRepeat || div.backgroundRepeat || "repeat",
          backgroundAttachment:
            div.hoveredbackgroundAttachment ||
            div.backgroundAttachment ||
            "scroll",

          fontFamily: div.hoveredfontFamily || div.fontFamily || "inherit",
          fontSize: div.hoveredfontSize || div.fontSize || "inherit",
          fontWeight: div.hoveredfontWeight || div.fontWeight || "normal",
          fontStyle: div.hoveredfontStyle || div.fontStyle || "normal",
          fontVariant: div.hoveredfontVariant || div.fontVariant || "normal",
          lineHeight: div.hoveredlineHeight || div.lineHeight || "normal",
          textShadow: div.hoveredtextShadow || div.textShadow || "none",
          textAlign: div.hoveredtextAlign || div.textAlign || "left",
          verticalAlign:
            div.hoveredverticalAlign || div.verticalAlign || "baseline",
          textDecoration:
            div.hoveredtextDecoration || div.textDecoration || "none",
          textTransform:
            div.hoveredtextTransform || div.textTransform || "none",
          letterSpacing:
            div.hoveredletterSpacing || div.letterSpacing || "normal",
          wordSpacing: div.hoveredwordSpacing || div.wordSpacing || "normal",
          textIndent: div.hoveredtextIndent || div.textIndent || "0",
          whiteSpace: div.hoveredwhiteSpace || div.whiteSpace || "normal",
          textOverflow: div.hoveredtextOverflow || div.textOverflow || "clip",
          direction: div.hovereddirection || div.direction || "ltr",
          unicodeBidi: div.hoveredunicodeBidi || div.unicodeBidi || "normal",
          writingMode:
            div.hoveredwritingMode || div.writingMode || "horizontal-tb",

          cursor: div.hoveredcursor || div.cursor || "default",
          filter: div.hoveredfilter || div.filter || "none",
          backdropFilter:
            div.hoveredbackdropFilter || div.backdropFilter || "none",
          mixBlendMode: div.hoveredmixBlendMode || div.mixBlendMode || "normal",
          aspectRatio: div.hoveredaspectRatio || div.aspectRatio || "auto",

          transform: div.hoveredtransform || div.transform || "none",
          transition: div.hoveredtransition || div.transition || "none",
        }
      : {};

  const activeStyles =
    isDivActive && div.pseudoClass === "active"
      ? {
          width: div.activatedwidth || div.width || "100%",
          height: div.hoveredheight || div.height || "auto",

          padding: div.activatedpadding || div.padding || "8px",
          paddingTop: div.activatedpaddingTop || div.activatedpadding || "8px",
          paddingRight:
            div.activatedpaddingRight || div.activatedpadding || "8px",
          paddingBottom:
            div.activatedpaddingBottom || div.activatedpadding || "8px",
          paddingLeft:
            div.activatedpaddingLeft || div.activatedpadding || "8px",
          margin: div.activatedmargin || div.margin || "0px",
          marginTop: div.activatedmarginTop || div.activatedmargin || "0px",
          marginRight: div.activatedmarginRight || div.activatedmargin || "0px",
          marginBottom:
            div.activatedmarginBottom || div.activatedmargin || "0px",
          marginLeft: div.activatedmarginLeft || div.activatedmargin || "0px",

          borderTop:
            div.activatedborderTop ||
            div.activatedborder ||
            div.border ||
            "1px solid black",
          borderRight:
            div.activatedborderRight ||
            div.activatedborder ||
            div.border ||
            "1px solid black",
          borderBottom:
            div.activatedborderBottom ||
            div.activatedborder ||
            div.border ||
            "1px solid black",
          borderLeft:
            div.activatedborderLeft ||
            div.activatedborder ||
            div.border ||
            "1px solid black",
          borderSapcing:
            div.activatedborderSpacing || div.borderSpacing || "0px",
          borderCollapse:
            div.activatedborderCollapse || div.borderCollapse || "0px",
          borderRadius: div.activatedborderRadius || div.borderRadius || "0",
          outline: div.activatedoutline || div.outline || "none",

          position: div.activatedposition || div.position || "relative",
          top: div.activatedtop || div.top || "auto",
          right: div.activatedright || div.right || "auto",
          bottom: div.activatedbottom || div.bottom || "auto",
          left: div.activatedleft || div.left || "auto",
          zIndex: div.activatedzIndex || div.zIndex || "auto",

          boxShadow: div.activatedboxShadow || div.boxShadow || "none",
          opacity: div.activatedOpacity || div.opacity || 1,
          display: div.activateddisplay || div.display || "block",
          flexDirection: div.activatedflexDirection ||  div.flexDirection || "column",
          flexWrap: div.activatedflexWrap || "nowrap",
          order: div.activatedorder || "0",
          flexGrow: div.activatedflexGrow || "0",
          flexShrink: div.activatedflexShrink || "1",
          flexBasis: div.activatedflexBasis || "auto",

          gridTemplateColumns: div.activatedgridTemplateColumns || "none",
          gridTemplateRows: div.activatedgridTemplateRows || "none",
          gridArea: div.activatedgridArea || "auto",
          gridAutoFlow: div.activatedgridAutoFlow || "row",
          gridAutoColumns: div.activatedgridAutoColumns || "auto",
          gridAutoRows: div.activatedgridAutoRows || "auto",
          columnGap: div.activatedcolumnGap || "0",
          rowGap: div.activatedrowGap || "0",

          justifyContent: div.activatedjustifyContent || "flex-start",
          alignItems: div.activatedalignItems || "stretch",
          alignContent: div.activatedalignContent || "stretch",
          alignSelf: div.activatedalignSelf || "auto",
         //  placeContent: div.activatedplaceContent || "normal",
         //  placeItems: div.activatedplaceItems || "normal",
          justifyItems: div.activatedjustifyItems || "stretch",
          justifySelf: div.activatedjustifySelf || "auto",

          gap: div.activatedgap || "0",
          visibility: div.activatedvisibility || "visible",

          color: div.activatedcolor || div.color || "#000000",
          backgroundColor:
            div.activatedbackgroundColor || div.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (div.activatedbackgroundImage) {
              if (div.activatedbackgroundImage.startsWith("./")) {
                const imageName = div.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${div.activatedbackgroundImage})`;
            }
            if (div.backgroundImage) {
              if (div.backgroundImage.startsWith("./")) {
                const imageName = div.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${div.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            div.activatedbackgroundPosition ||
            div.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            div.activatedbackgroundSize || div.backgroundSize || "auto",
          backgroundRepeat:
            div.activatedbackgroundRepeat || div.backgroundRepeat || "repeat",
          backgroundAttachment:
            div.activatedbackgroundAttachment ||
            div.backgroundAttachment ||
            "scroll",

          fontFamily: div.activatedfontFamily || div.fontFamily || "inherit",
          fontSize: div.activatedfontSize || div.fontSize || "inherit",
          fontWeight: div.activatedfontWeight || div.fontWeight || "normal",
          fontStyle: div.activatedfontStyle || div.fontStyle || "normal",
          fontVariant: div.activatedfontVariant || div.fontVariant || "normal",
          lineHeight: div.activatedlineHeight || div.lineHeight || "normal",
          textShadow: div.activatedtextShadow || div.textShadow || "none",
          textAlign: div.activatedtextAlign || div.textAlign || "left",
          verticalAlign:
            div.activatedverticalAlign || div.verticalAlign || "baseline",
          textDecoration:
            div.activatedtextDecoration || div.textDecoration || "none",
          textTransform:
            div.activatedtextTransform || div.textTransform || "none",
          letterSpacing:
            div.activatedletterSpacing || div.letterSpacing || "normal",
          wordSpacing: div.activatedwordSpacing || div.wordSpacing || "normal",
          textIndent: div.activatedtextIndent || div.textIndent || "0",
          whiteSpace: div.activatedwhiteSpace || div.whiteSpace || "normal",
          textOverflow: div.activatedtextOverflow || div.textOverflow || "clip",
          direction: div.activateddirection || div.direction || "ltr",
          unicodeBidi: div.activatedunicodeBidi || div.unicodeBidi || "normal",
          writingMode:
            div.activatedwritingMode || div.writingMode || "horizontal-tb",

          cursor: div.activatedcursor || div.cursor || "default",
          filter: div.activatedfilter || div.filter || "none",
          backdropFilter:
            div.activatedbackdropFilter || div.backdropFilter || "none",
          mixBlendMode:
            div.activatedmixBlendMode || div.mixBlendMode || "normal",
          aspectRatio: div.activatedaspectRatio || div.aspectRatio || "auto",

          transform: div.activatedtransform || div.transform || "none",
          transition: div.activatedtransition || div.transition || "none",
        }
      : {};

  const divStyle = {
    width: div.width || "100%",
    height: div.height || "auto",
    padding: div.padding || "8px",
    paddingTop: div.paddingTop || div.padding || "8px",
    paddingRight: div.paddingRight || div.padding || "8px",
    paddingBottom: div.paddingBottom || div.padding || "8px",
    paddingLeft: div.paddingLeft || div.padding || "8px",
    margin: div.margin || "0px",
    marginTop: div.marginTop || div.margin || "0px",
    marginRight: div.marginRight || div.margin || "0px",
    marginBottom: div.marginBottom || div.margin || "0px",
    marginLeft: div.marginLeft || div.margin || "0px",

    borderTop: div.borderTop || div.border || "1px solid #000000",
    borderRight: div.borderRight || div.border || "1px solid #000000",
    borderBottom: div.borderBottom || div.border || "1px solid #000000",
    borderLeft: div.borderLeft || div.border || "1px solid #000000",

    borderSapcing: div.borderSpacing || "0px",
    borderCollapse: div.borderCollapse || "0px",
    borderRadius: div.borderRadius || "0",
    boxSizing: div.boxSizing || "border-box",

    position: div.position || "relative",
    top: div.top || "auto",
    right: div.right || "auto",
    bottom: div.bottom || "auto",
    left: div.left || "auto",
    zIndex: div.zIndex || "auto",
    opacity:div.opacity || "1",

    display: div.display || "block",
    flexDirection: div.flexDirection || "column",
    flexWrap: div.flexWrap || "nowrap",
    order: div.order || "0",
    flexGrow: div.flexGrow || "0",
    flexShrink: div.flexShrink || "1",
    flexBasis: div.flexBasis || "auto",

    gridTemplateColumns: div.gridTemplateColumns || "none",
    gridTemplateRows: div.gridTemplateRows || "none",
    gridArea: div.gridArea || "auto",
    gridAutoFlow: div.gridAutoFlow || "row",
    gridAutoColumns: div.gridAutoColumns || "auto",
    gridAutoRows: div.gridAutoRows || "auto",
    columnGap: div.columnGap || "0",
    rowGap: div.rowGap || "0",

   //  placeContent: div.placeContent || "normal",
   //  placeItems: div.placeItems || "normal",

    justifyContent: div.justifyContent || "flex-start",
    alignItems: div.alignItems || "stretch",
    alignContent: div.alignContent || "stretch",
    alignSelf: div.alignSelf || "auto",
    justifyItems: div.justifyItems || "stretch",
    justifySelf: div.justifySelf || "auto",

    gap: div.gap || "0",
    visibility: div.visibility || "visible",

    color: div.color || "#000000",
    backgroundColor: isHovered ? "#ADD8E6" : div.backgroundColor || "#ffffff",

    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: div.backgroundPosition || "0% 0%",
    backgroundSize: div.backgroundSize || "auto",
    backgroundRepeat: div.backgroundRepeat || "repeat",
    backgroundAttachment: div.backgroundAttachment || "scroll",

    boxShadow: div.boxShadow || "none",
    filter: div.filter || "none",
    backdropFilter: div.backdropFilter || "none",
    mixBlendMode: div.mixBlendMode || "normal",
    aspectRatio: div.aspectRatio || "auto",

    resize: div.resize || "none",
    userSelect: div.userSelect || "auto",
    overflow: div.resize && div.resize !== "none" ? "auto" : "visible",
    overflowX: div.overflowX || "hidden",
    overflowY: div.overflowY || "hidden",

    fontSize: div.fontSize || "inherit",
    fontFamily: div.fontFamily || "inherit",
    fontWeight: div.fontWeight || "normal",
    fontStyle: div.fontStyle || "normal",
    fontVariant: div.fontVariant || "normal",
    lineHeight: div.lineHeight || "normal",
    textAlign: div.textAlign || "left",
    textDecoration: div.textDecoration || "none",
    textTransform: div.textTransform || "none",
    textShadow: div.textShadow || "none",
    verticalAlign: div.verticalAlign || "baseline",
    letterSpacing: div.letterSpacing || "normal",
    wordSpacing: div.wordSpacing || "normal",
    textIndent: div.textIndent || "0",
    whiteSpace: div.whiteSpace || "normal",
    textOverflow: div.textOverflow || "clip",
    direction: div.direction || "ltr",
    unicodeBidi: div.unicodeBidi || "normal",
    writingMode: div.writingMode || "horizontal-tb",
    cursor: div.cursor || "default",
    outline: div.outline || "none",

    transform: div.transform || "none",
    transition:
      div.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };

  return (
    <div
      className="Main_Division"
      ref={combinedRef}
      style={divStyle}
      onMouseEnter={() => setIsDivHovered(true)}
      onMouseLeave={() => setIsDivHovered(false)}
      onFocus={() => setIsDivFocused(true)}
      onBlur={() => setIsDivFocused(false)}
      onMouseDown={() => setIsDivActive(true)}
      onMouseUp={() => setIsDivActive(false)}
      tabIndex={0}
    >
      {div.children &&
        div.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </div>
  );
};

export default Division;
